
export function startCountdown(seconds = 180, onTick: (time: string) => void, onComplete: () => void) {
  let remainingSeconds = seconds;

  const timer = setInterval(() => {
    remainingSeconds--;

    const minutes = Math.floor(remainingSeconds / 60);
    const secs = remainingSeconds % 60;
    const timeString = `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

    onTick(timeString);

    if (remainingSeconds <= 0) {
      clearInterval(timer);
      onComplete();
    }
  }, 1000);

  return timer;
}

export function stringToDate(val: string) {
  const data = new Date(val)
  return data
}

export function termDay(startDate = '', enddate = '') {
  if (startDate === '' || enddate === '') {
    return 1000
  }

  const sdt = stringToDate(startDate) // IOS 때문에 초기화
  const edt = new Date(enddate)

  const aa = edt.getTime() - sdt.getTime()
  const bb = Math.floor(aa / (1000 * 3600 * 24))

  return bb
}

export const sleepTime = async (number: number) => {
  await new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve() // return 대신 resolve 호출
    }, number)
  })
}

// timestamp to 오직 HH:MM:SS
export const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp)
  return date.toTimeString().slice(0, 8)
}

// 타입스탬프를 "YYYY-MM-DD" 으로 변환
export function formatTimestampToDate(timestamp: string): string {
  const date = new Date(Number(timestamp))

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

// x 초 뒤에서 실행하는 보조 도구
export function throttle<T extends (...args: any[]) => any>(func: T, limit: number): (...args: Parameters<T>) => void {
  let inThrottle: boolean

  return function (this: any, ...args: Parameters<T>) {
    const context = this
    if (!inThrottle) {
      func.apply(context, args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}

// 로컬 시간에 맞춰서 변경해서 보여주는 장치
export const convertUnixToLocalDateTime = (unixTime: number | string): string => {
  const date = new Date(typeof unixTime === 'string' ? parseInt(unixTime) : unixTime)

  const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US'

  const localDateTime = date.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  return localDateTime.replace(',', '')
}

// 유닉스 타임을 넣으면 time 형태로 전달 ( 15:06:12 )
export const convertTimestampToTime = (timestamp: number | string) => {
  const parsedTimestamp = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp

  const date = new Date(parsedTimestamp)

  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')

  return `${hours}:${minutes}:${seconds}`
}

// 밀리초 단위 타임스탬프를 YYYY-MM-DD HH:MM:SS 로 변환
export function formatTimestampToDateTime(timestamp: string | number) {
  const date = new Date(Number(timestamp))

  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')
  const seconds = String(date.getUTCSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

// UTC+0 타임스탬프를 UTC+9로 변환하는 함수
export function convertUTC0ToUTC9(timestamp: string | number): number {
  const UTC9_OFFSET = 9 * 60 * 60 * 1000
  return Number(timestamp) + UTC9_OFFSET
}

// UTC+0 타임스탬프를 UTC+9 날짜시간 문자열로 변환하는 함수
export function formatUTC0ToUTC9DateTime(timestamp: string | number): string {
  const utc9Timestamp = convertUTC0ToUTC9(timestamp)
  const date = new Date(utc9Timestamp)

  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')
  const seconds = String(date.getUTCSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}


export function convertDateTimeStringToKoreaTime(dateTimeString: string): number {
  let safeDateString = dateTimeString
  if (typeof dateTimeString === 'string' && !dateTimeString.includes('Z')) {
    safeDateString = dateTimeString.replace(' ', 'T') + 'Z'
  }

  const date = new Date(safeDateString)
  const utc0Timestamp = date.getTime()
  
  return convertUTC0ToUTC9(utc0Timestamp)
}

// 한국 시간 문자열로 변환하는 함수 (무조건 KST로 표시)
export function formatDateTimeStringToKoreaTimeString(dateTimeString: string): string {
  // 1. 문자열을 UTC TimeStamp로 변환
  let safeDateString = dateTimeString
  if (typeof dateTimeString === 'string' && !dateTimeString.includes('Z')) {
    safeDateString = dateTimeString.replace(' ', 'T') + 'Z'
  }

  const date = new Date(safeDateString)
  const utc0Timestamp = date.getTime()

  // 2. UTC+9 변환 및 포맷팅 (여기서 9시간 더함)
  return formatUTC0ToUTC9DateTime(utc0Timestamp)
}

// 현지 시간 문자열로 변환하는 함수
export function formatDateTimeStringToLocalString(dateTimeString: string): string {
  const date = new Date(dateTimeString)

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}
